import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosLogistics } from '../axios/api';

// ─── Thunks ──────────────────────────────────────────────────────────────────

export const fetchSeaVendors = createAsyncThunk(
  'seaVendors/fetchSeaVendors',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') params.append(key, value);
      });
      const response = await axiosLogistics.get(`/logistics/sea-vendors?${params}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch sea vendors');
    }
  }
);

export const fetchSeaVendorById = createAsyncThunk(
  'seaVendors/fetchSeaVendorById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosLogistics.get(`/logistics/sea-vendors/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch sea vendor');
    }
  }
);

export const createSeaVendor = createAsyncThunk(
  'seaVendors/createSeaVendor',
  async (vendorData, { rejectWithValue }) => {
    try {
      const response = await axiosLogistics.post('/logistics/sea-vendors', vendorData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to create sea vendor' });
    }
  }
);

export const updateSeaVendor = createAsyncThunk(
  'seaVendors/updateSeaVendor',
  async ({ id, vendorData }, { rejectWithValue }) => {
    try {
      const response = await axiosLogistics.put(`/logistics/sea-vendors/${id}`, vendorData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update sea vendor');
    }
  }
);

export const deleteSeaVendor = createAsyncThunk(
  'seaVendors/deleteSeaVendor',
  async (id, { rejectWithValue }) => {
    try {
      await axiosLogistics.delete(`/logistics/sea-vendors/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete sea vendor');
    }
  }
);

// ─── Contact Thunks ──────────────────────────────────────────────────────────

export const addSeaVendorContact = createAsyncThunk(
  'seaVendors/addContact',
  async ({ vendorId, name, phone, email, position }, { rejectWithValue }) => {
    try {
      const res = await axiosLogistics.post(`/logistics/sea-vendors/${vendorId}/contacts`, { name, phone, email, position });
      return { vendorId, contact: res.data.data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add contact');
    }
  }
);

export const updateSeaVendorContact = createAsyncThunk(
  'seaVendors/updateContact',
  async ({ vendorId, contactId, name, phone, email, position }, { rejectWithValue }) => {
    try {
      const res = await axiosLogistics.put(`/logistics/sea-vendors/${vendorId}/contacts/${contactId}`, { name, phone, email, position });
      return { vendorId, contact: res.data.data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update contact');
    }
  }
);

export const removeSeaVendorContact = createAsyncThunk(
  'seaVendors/removeContact',
  async ({ vendorId, contactId }, { rejectWithValue }) => {
    try {
      await axiosLogistics.delete(`/logistics/sea-vendors/${vendorId}/contacts/${contactId}`);
      return { vendorId, contactId };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to remove contact');
    }
  }
);

// ─── Contact Detail Thunks (multiple phone/email pairs per contact) ──────────

export const addSeaVendorContactDetail = createAsyncThunk(
  'seaVendors/addContactDetail',
  async ({ vendorId, contactId, phone, email }, { rejectWithValue }) => {
    try {
      const res = await axiosLogistics.post(`/logistics/sea-vendors/${vendorId}/contacts/${contactId}/details`, { phone, email });
      return { vendorId, contactId, detail: res.data.data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add contact detail');
    }
  }
);

export const updateSeaVendorContactDetail = createAsyncThunk(
  'seaVendors/updateContactDetail',
  async ({ vendorId, contactId, detailId, phone, email }, { rejectWithValue }) => {
    try {
      const res = await axiosLogistics.put(`/logistics/sea-vendors/${vendorId}/contacts/${contactId}/details/${detailId}`, { phone, email });
      return { vendorId, contactId, detail: res.data.data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update contact detail');
    }
  }
);

export const removeSeaVendorContactDetail = createAsyncThunk(
  'seaVendors/removeContactDetail',
  async ({ vendorId, contactId, detailId }, { rejectWithValue }) => {
    try {
      await axiosLogistics.delete(`/logistics/sea-vendors/${vendorId}/contacts/${contactId}/details/${detailId}`);
      return { vendorId, contactId, detailId };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to remove contact detail');
    }
  }
);

// ─── Initial State ────────────────────────────────────────────────────────────

const initialState = {
  vendors: [],
  selectedVendor: null,
  pagination: { currentPage: 1, totalPages: 1, totalCount: 0, limit: 50 },
  filters: { page: 1, limit: 50, search: '', vendor_status: '', vendor_type: '', country: '', port_code: '' },
  loading: false,
  creating: false,
  updating: false,
  deleting: false,
  error: null,
};

// ─── Slice ────────────────────────────────────────────────────────────────────

const seaVendorSlice = createSlice({
  name: 'seaVendors',
  initialState,
  reducers: {
    selectSeaVendor:        (state, action) => { state.selectedVendor = action.payload; },
    clearSelectedSeaVendor: (state) => { state.selectedVendor = null; },
    updateSeaFilters:       (state, action) => { state.filters = { ...state.filters, ...action.payload }; },
    resetSeaFilters:        (state) => { state.filters = initialState.filters; },
    clearSeaError:          (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSeaVendors.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(fetchSeaVendors.fulfilled, (state, { payload }) => { state.loading = false; state.vendors = payload.vendors; state.pagination = payload.pagination; })
      .addCase(fetchSeaVendors.rejected,  (state, { payload }) => { state.loading = false; state.error = payload; })

      .addCase(fetchSeaVendorById.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(fetchSeaVendorById.fulfilled, (state, { payload }) => { state.loading = false; state.selectedVendor = payload; })
      .addCase(fetchSeaVendorById.rejected,  (state, { payload }) => { state.loading = false; state.error = payload; })

      .addCase(createSeaVendor.pending,   (state) => { state.creating = true; state.error = null; })
      .addCase(createSeaVendor.fulfilled, (state, { payload }) => { state.creating = false; state.vendors.unshift(payload); state.pagination.totalCount += 1; })
      .addCase(createSeaVendor.rejected,  (state, { payload }) => { state.creating = false; state.error = payload; })

      .addCase(updateSeaVendor.pending,   (state) => { state.updating = true; state.error = null; })
      .addCase(updateSeaVendor.fulfilled, (state, { payload }) => {
        state.updating = false;
        const i = state.vendors.findIndex(v => v.id === payload.id);
        if (i !== -1) state.vendors[i] = payload;
        if (state.selectedVendor?.id === payload.id) state.selectedVendor = { ...state.selectedVendor, ...payload };
      })
      .addCase(updateSeaVendor.rejected,  (state, { payload }) => { state.updating = false; state.error = payload; })

      .addCase(deleteSeaVendor.pending,   (state) => { state.deleting = true; state.error = null; })
      .addCase(deleteSeaVendor.fulfilled, (state, { payload }) => {
        state.deleting = false;
        state.vendors = state.vendors.filter(v => v.id !== payload);
        state.pagination.totalCount -= 1;
        if (state.selectedVendor?.id === payload) state.selectedVendor = null;
      })
      .addCase(deleteSeaVendor.rejected,  (state, { payload }) => { state.deleting = false; state.error = payload; })

      // Contacts
      .addCase(addSeaVendorContact.fulfilled, (state, { payload }) => {
        if (state.selectedVendor?.id === payload.vendorId) {
          if (!state.selectedVendor.contacts) state.selectedVendor.contacts = [];
          state.selectedVendor.contacts.push(payload.contact);
        }
      })
      .addCase(updateSeaVendorContact.fulfilled, (state, { payload }) => {
        if (state.selectedVendor?.id === payload.vendorId) {
          const i = (state.selectedVendor.contacts || []).findIndex(c => c.id === payload.contact.id);
          if (i !== -1) state.selectedVendor.contacts[i] = payload.contact;
        }
      })
      .addCase(removeSeaVendorContact.fulfilled, (state, { payload }) => {
        if (state.selectedVendor?.id === payload.vendorId) {
          state.selectedVendor.contacts = (state.selectedVendor.contacts || []).filter(c => c.id !== payload.contactId);
        }
      })

      // Contact details
      .addCase(addSeaVendorContactDetail.fulfilled, (state, { payload }) => {
        if (state.selectedVendor?.id === payload.vendorId) {
          const c = (state.selectedVendor.contacts || []).find(c => c.id === payload.contactId);
          if (c) { if (!c.details) c.details = []; c.details.push(payload.detail); }
        }
      })
      .addCase(updateSeaVendorContactDetail.fulfilled, (state, { payload }) => {
        if (state.selectedVendor?.id === payload.vendorId) {
          const c = (state.selectedVendor.contacts || []).find(c => c.id === payload.contactId);
          if (c) {
            const i = (c.details || []).findIndex(d => d.id === payload.detail.id);
            if (i !== -1) c.details[i] = payload.detail;
          }
        }
      })
      .addCase(removeSeaVendorContactDetail.fulfilled, (state, { payload }) => {
        if (state.selectedVendor?.id === payload.vendorId) {
          const c = (state.selectedVendor.contacts || []).find(c => c.id === payload.contactId);
          if (c) c.details = (c.details || []).filter(d => d.id !== payload.detailId);
        }
      });
  },
});

export const { selectSeaVendor, clearSelectedSeaVendor, updateSeaFilters, resetSeaFilters, clearSeaError } = seaVendorSlice.actions;

export const selectSeaVendors          = (state) => state.seaVendors.vendors;
export const selectSelectedSeaVendor   = (state) => state.seaVendors.selectedVendor;
export const selectSeaVendorContacts   = (state) => state.seaVendors.selectedVendor?.contacts || [];
export const selectSeaPagination       = (state) => state.seaVendors.pagination;
export const selectSeaFilters          = (state) => state.seaVendors.filters;
export const selectSeaLoading          = (state) => state.seaVendors.loading;
export const selectSeaCreating         = (state) => state.seaVendors.creating;
export const selectSeaUpdating         = (state) => state.seaVendors.updating;
export const selectSeaDeleting         = (state) => state.seaVendors.deleting;
export const selectSeaError            = (state) => state.seaVendors.error;

export default seaVendorSlice.reducer;
